import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { XCircle } from 'lucide-react';
import { useCart } from '../store/cartStore';

const palette = {
  shell: 'radial-gradient(circle at 50% 0%, #1a1d2d 0%, #040509 100%)',
  panel: 'rgba(20, 22, 32, 0.7)',
  panelBorder: 'rgba(255, 255, 255, 0.08)',
  ink: '#f5f8ff',
  muted: 'rgba(245, 248, 255, 0.65)',
  danger: '#ff4d4d',
  dangerGlow: '0 0 20px rgba(255, 77, 77, 0.15)',
  surface: 'rgba(255, 255, 255, 0.03)',
  surfaceHover: 'rgba(255, 255, 255, 0.06)'
};

const PageShell = styled.div`
  min-height: 100vh;
  padding: 2rem;
  padding-top: 120px;
  background: ${palette.shell};
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const ModalCard = styled.div`
  width: min(480px, 100%);
  background: ${palette.panel};
  border-radius: 24px;
  border: 1px solid ${palette.panelBorder};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 77, 77, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  box-shadow: ${palette.dangerGlow};
  border: 1px solid rgba(255, 77, 77, 0.2);
  
  svg {
    width: 40px;
    height: 40px;
    color: ${palette.danger};
  }
`;

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Heading = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${palette.ink};
`;

const Message = styled.p`
  margin: 0;
  color: ${palette.muted};
  font-size: 1rem;
  line-height: 1.6;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 500;
  background: ${palette.surface};
  border: 1px solid ${palette.panelBorder};
  color: ${palette.muted};
  gap: 0.5rem;
  margin-top: 0.5rem;

  &::before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $loading }) => $loading ? '#fbbf24' : palette.muted};
    box-shadow: ${({ $loading }) => $loading ? '0 0 8px #fbbf24' : 'none'};
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 400px) {
    flex-direction: row;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.85rem 1.5rem;
  border-radius: 14px;
  border: 1px solid ${({ $primary }) => ($primary ? 'transparent' : palette.panelBorder)};
  background: ${({ $primary }) =>
    $primary
      ? 'linear-gradient(135deg, #0a84ff 0%, #004de0 100%)'
      : palette.surface};
  color: ${({ $primary }) => ($primary ? '#fff' : palette.ink)};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${({ $primary }) =>
    $primary
      ? 'linear-gradient(135deg, #1e8eff 0%, #0056f5 100%)'
      : palette.surfaceHover};
    box-shadow: ${({ $primary }) =>
    $primary ? '0 8px 20px -6px rgba(10, 132, 255, 0.6)' : 'none'};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CheckoutCancelPage = () => {
  const navigate = useNavigate();
  const [isAbandoning, setIsAbandoning] = useState(false);
  const { loadCartFromBackend } = useCart();

  useEffect(() => {
    const abandonCheckout = async () => {
      // Get order ID from localStorage (should be set when checkout started)
      const pendingOrderId = localStorage.getItem('pendingOrderId');

      if (!pendingOrderId) {
        return;
      }

      setIsAbandoning(true);

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          return;
        }

        console.log('🚨 User cancelled Stripe checkout, abandoning order:', pendingOrderId);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/checkout/${pendingOrderId}/abandon`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            }
          }
        );

        if (response.ok) {
          console.log('✅ Successfully abandoned checkout');
          localStorage.removeItem('pendingOrderId');
          await loadCartFromBackend();
        }

      } catch (error) {
        console.error('❌ Error abandoning checkout:', error);
      } finally {
        setIsAbandoning(false);
      }
    };

    const timeoutId = setTimeout(abandonCheckout, 500); // Small delay for UX smoothness
    return () => clearTimeout(timeoutId);
  }, [loadCartFromBackend]);

  return (
    <PageShell>
      <ModalCard>
        <IconWrapper>
          <XCircle />
        </IconWrapper>

        <ContentBlock>
          <Heading>Checkout Cancelled</Heading>
          <Message>
            No charges were made. Your payment flow has been cancelled.
            {isAbandoning
              ? ' We are releasing your ticket reservations...'
              : ' Your ticket reservations have been released and the cart reset.'}
          </Message>
        </ContentBlock>

        <StatusPill $loading={isAbandoning}>
          {isAbandoning ? 'Processing cancellation...' : 'Cancellation complete'}
        </StatusPill>

        <ActionRow>
          <ActionButton onClick={() => navigate('/events')}>
            Browse Events
          </ActionButton>
          <ActionButton $primary onClick={() => navigate('/cart')}>
            Return to Cart
          </ActionButton>
        </ActionRow>
      </ModalCard>
    </PageShell>
  );
};

export default CheckoutCancelPage;
